import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuRadioGroup,
  ContextMenuRadioItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '@crowdex/ui/components/context-menu';
import { ArrowDown, ArrowLeft, ArrowRight, ArrowUp, PinOff } from 'lucide-react';
import {
  type CSSProperties,
  type ReactElement,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { createPortal } from 'react-dom';
import type { FavoriteItem, Favorites } from './favorites';
import { FAVORITE_COLUMNS, type FavoritesDensity } from './sidebar-preferences';

/*
 * The favourites change in place, dock-style (no edit mode): a tile pressed and dragged moves,
 * the others making room; dragged out of the tiles and dropped, it is unpinned (with an Undo).
 * An app of All apps dragged onto the tiles is pinned where it is dropped. A mouse drags once it
 * has moved a few pixels (less is a click); a finger rests on the tile first (a long press),
 * then drags; a long press let go without moving opens the tiles' menu. The menu (right-click)
 * moves and unpins a tile and sets the tiles per row; Alt + arrows move the focused tile.
 */

/** How far a pointer moves before it drags (less is a click). */
export const DRAG_THRESHOLD = 5;
/** How long a finger rests on a tile before it lifts it (then it drags, or opens the menu). */
export const LONG_PRESS = 400;
/** How far a finger may move before the long press (more is a scroll). */
const TOUCH_TOLERANCE = 10;
/** How far around the tiles a drop still lands among them. */
const ZONE_MARGIN = 12;
/** Half the ghost: it is centred under the pointer. */
const GHOST_HALF = 21;

export type FavoriteDragOrigin = 'favorites' | 'apps';

type Over = { zone: string; index: number };
type Session = { entry: string; from: FavoriteDragOrigin; owner: string; over: Over | null };

let session: Session | null = null;
const listeners = new Set<() => void>();
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
function setSession(next: Session | null): void {
  session = next;
  for (const listener of listeners) listener();
}
const useSession = () =>
  useSyncExternalStore(
    subscribe,
    () => session,
    () => null,
  );

/** The tiles a drag may land on (the sidebar's, the rail's, the drawer's). */
type Zone = {
  element: RefObject<HTMLElement | null>;
  indexAt: (x: number, y: number, entry: string) => number;
};
const zones = new Map<string, Zone>();

function locate(x: number, y: number, entry: string): Over | null {
  for (const [id, zone] of zones) {
    const element = zone.element.current;
    if (element === null) continue;
    const rect = element.getBoundingClientRect();
    // A surface out of sight (the sidebar collapsed) takes nothing.
    if (rect.width === 0 && rect.height === 0) continue;
    if (
      x >= rect.left - ZONE_MARGIN &&
      x <= rect.right + ZONE_MARGIN &&
      y >= rect.top - ZONE_MARGIN &&
      y <= rect.bottom + ZONE_MARGIN
    )
      return { zone: id, index: zone.indexAt(x, y, entry) };
  }
  return null;
}

/**
 * Where among the tiles of `element` a drop at (x, y) lands: by the nearest tile, as laid out
 * (`measure` gives a gliding tile's place, not where its animation shows it).
 */
function slotIndex(
  element: HTMLElement,
  x: number,
  y: number,
  entry: string,
  measure: (slot: HTMLElement) => DOMRect,
): number {
  const slots = [...element.querySelectorAll<HTMLElement>('[data-favorite-slot]')];
  let nearest = -1;
  let best = Number.POSITIVE_INFINITY;
  let after = false;
  slots.forEach((slot, index) => {
    const rect = measure(slot);
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const distance = Math.hypot(x - cx, y - cy);
    if (distance < best) {
      best = distance;
      nearest = index;
      // Past its middle (along its row, or below it): the drop goes after it.
      after = Math.abs(y - cy) <= rect.height / 2 ? x > cx : y > cy;
    }
  });
  if (nearest === -1) return 0;
  // The dragged one holds its place among them: the nearest is where it goes.
  if (slots.some((slot) => slot.dataset['favoriteSlot'] === entry)) return nearest;
  return nearest + (after ? 1 : 0);
}

const sameOver = (left: Over | null, right: Over | null) =>
  left?.zone === right?.zone && left?.index === right?.index;

let swallowClick = false;
/** After a drag (or a long press that opened the menu), the click its release makes is not one. */
function swallowNextClick(): void {
  swallowClick = true;
  window.setTimeout(() => {
    swallowClick = false;
  }, 0);
}
/** True (once) when the click under way ends a drag: the tile does not open. */
export function consumeDragClick(): boolean {
  if (!swallowClick) return false;
  swallowClick = false;
  return true;
}

/** A finger is on a tile: its long press is the drag's (or the menu's), not the browser's. */
let touchPress = false;
/** The menu a long press opens on its release (let through, unlike the browser's own). */
let openingMenu = false;

/**
 * While a tile drags, the page is not pointed at: a sheet over the whole window (the drag's
 * overlay) takes the pointer, so nothing else lights up on hover (rows, tooltips), no frame (an
 * app's iframe) takes the moves and the release clicks nothing. The pointer grabs, no text is
 * selected; the drop targets are found by their boxes. `data-favorite-dragging` says so.
 */
function markDragging(on: boolean): void {
  const root = document.documentElement;
  if (on) root.setAttribute('data-favorite-dragging', '');
  else root.removeAttribute('data-favorite-dragging');
  root.style.cursor = on ? 'grabbing' : '';
  root.style.userSelect = on ? 'none' : '';
}

/** Electron moves the window from a drag region: never from the tiles, the apps or the drag. */
export const NO_WINDOW_DRAG = {
  WebkitAppRegion: 'no-drag',
  appRegion: 'no-drag',
} as unknown as CSSProperties;

const reducedMotion = () =>
  globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

export type DragPayload = {
  entry: string;
  title: string;
  /** What the ghost shows under the pointer (the tile's icon). */
  icon: ReactNode;
  from: FavoriteDragOrigin;
};

/**
 * Drags tiles (or All apps' apps) onto the favourites: `begin` on a pointer down; the drop moves,
 * pins or unpins. `ghost` (rendered by the caller) follows the pointer; `held` is the tile a
 * finger lifted (its long press), before it moves.
 */
export function useFavoriteDragSource(favorites: Favorites) {
  const owner = useId();
  const latest = useRef(favorites);
  latest.current = favorites;
  const [ghost, setGhost] = useState<(DragPayload & { pointerId: number }) | null>(null);
  const [held, setHeld] = useState<string | null>(null);
  const ghostElement = useRef<HTMLDivElement | null>(null);
  const start = useRef({ x: 0, y: 0 });
  const end = useRef<() => void>(() => {});
  useEffect(() => () => end.current(), []);

  const drop = (entry: string, from: FavoriteDragOrigin, over: Over | null) => {
    const { move, insert, unpin, isFavorite } = latest.current;
    if (over === null) {
      if (from === 'favorites') unpin(entry);
      return;
    }
    if (from === 'favorites' && isFavorite(entry)) move(entry, over.index);
    else insert(entry, over.index);
  };

  const begin = (event: ReactPointerEvent<HTMLElement>, payload: DragPayload) => {
    if (session !== null) return;
    const touch = event.pointerType === 'touch';
    if (!touch && event.button !== 0) return;
    // All apps' list scrolls under a finger: its apps drag with a mouse (or a pen).
    if (touch && payload.from === 'apps') return;
    end.current();
    // A new press: the click a previous drop swallowed is long gone.
    swallowClick = false;
    const target = event.currentTarget;
    const pointerId = event.pointerId;
    start.current = { x: event.clientX, y: event.clientY };
    let state: 'pressed' | 'waiting' | 'held' | 'dragging' = touch ? 'waiting' : 'pressed';
    touchPress = touch;
    const timer = touch
      ? window.setTimeout(() => {
          state = 'held';
          navigator.vibrate?.(10);
          setHeld(payload.entry);
        }, LONG_PRESS)
      : null;

    const place = (x: number, y: number) => {
      const element = ghostElement.current;
      if (element !== null)
        element.style.transform = `translate(${x - GHOST_HALF}px, ${y - GHOST_HALF}px)`;
    };
    const onMove = (moved: PointerEvent) => {
      if (moved.pointerId !== pointerId) return;
      const distance = Math.hypot(moved.clientX - start.current.x, moved.clientY - start.current.y);
      if (state === 'waiting') {
        // A finger that moves before its long press scrolls.
        if (distance > TOUCH_TOLERANCE) finish();
        return;
      }
      if (state !== 'dragging') {
        if (distance < DRAG_THRESHOLD) return;
        state = 'dragging';
        markDragging(true);
        setHeld(null);
        start.current = { x: moved.clientX, y: moved.clientY };
        setGhost({ ...payload, pointerId });
        setSession({
          entry: payload.entry,
          from: payload.from,
          owner,
          over: locate(moved.clientX, moved.clientY, payload.entry),
        });
      }
      start.current = { x: moved.clientX, y: moved.clientY };
      place(moved.clientX, moved.clientY);
      const over = locate(moved.clientX, moved.clientY, payload.entry);
      if (session !== null && !sameOver(over, session.over)) setSession({ ...session, over });
    };
    const onUp = (released: PointerEvent) => {
      if (released.pointerId !== pointerId) return;
      const ended = state;
      const over = session?.over ?? null;
      finish();
      if (ended === 'dragging') {
        swallowNextClick();
        drop(payload.entry, payload.from, over);
      } else if (ended === 'held') {
        // A long press let go where it was: the tiles' menu, on that tile.
        swallowNextClick();
        openingMenu = true;
        target.dispatchEvent(
          new MouseEvent('contextmenu', {
            bubbles: true,
            cancelable: true,
            clientX: released.clientX,
            clientY: released.clientY,
          }),
        );
        openingMenu = false;
      }
    };
    const onCancel = (cancelled: PointerEvent) => {
      if (cancelled.pointerId === pointerId) finish();
    };
    const onKey = (key: KeyboardEvent) => {
      if (key.key !== 'Escape' || state !== 'dragging') return;
      key.preventDefault();
      finish();
      // The release of a cancelled drag is not a click either: the tile does not open.
      const swallowRelease = (released: PointerEvent) => {
        if (released.pointerId !== pointerId) return;
        window.removeEventListener('pointerup', swallowRelease, true);
        swallowNextClick();
      };
      window.addEventListener('pointerup', swallowRelease, true);
    };
    // The browser's own drag (an image, a selected text, a link under the press) would take the
    // moves from us (a pointercancel): a press here drags a tile, nothing else.
    const onNativeDrag = (dragged: DragEvent) => dragged.preventDefault();
    // Once the finger lifted the tile, its moves drag it: the page does not scroll.
    const onTouchMove = (moved: TouchEvent) => {
      if (state === 'held' || state === 'dragging') moved.preventDefault();
    };
    function finish() {
      if (timer !== null) window.clearTimeout(timer);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      window.removeEventListener('keydown', onKey, true);
      document.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('dragstart', onNativeDrag, true);
      touchPress = false;
      if (state === 'dragging') markDragging(false);
      if (session?.owner === owner) setSession(null);
      setGhost(null);
      setHeld(null);
      end.current = () => {};
    }
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    window.addEventListener('keydown', onKey, true);
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('dragstart', onNativeDrag, true);
    end.current = finish;
  };

  const ghostNode =
    ghost === null || typeof document === 'undefined'
      ? null
      : createPortal(
          <>
            <div
              aria-hidden
              className="fixed inset-0 z-drag cursor-grabbing"
              data-electron-no-drag-region
              data-favorite-drag-overlay
              ref={(element) => {
                // It takes the pointer's moves from here on, wherever they go (out of the window).
                try {
                  element?.setPointerCapture?.(ghost.pointerId);
                } catch {
                  // A pointer already released: nothing to take.
                }
              }}
              style={NO_WINDOW_DRAG}
            />
            <DragGhost
              from={ghost.from}
              icon={ghost.icon}
              initial={start.current}
              refCallback={(element) => {
                ghostElement.current = element;
              }}
              title={ghost.title}
            />
          </>,
          document.body,
        );
  return { begin, ghost: ghostNode, held };
}

/** The tile under the pointer: calm (no colour), saying "Unpin" once out of the tiles. */
function DragGhost({
  icon,
  title,
  from,
  initial,
  refCallback,
}: {
  icon: ReactNode;
  title: string;
  from: FavoriteDragOrigin;
  initial: { x: number; y: number };
  refCallback: (element: HTMLDivElement | null) => void;
}) {
  const current = useSession();
  const leaving = from === 'favorites' && current !== null && current.over === null;
  return (
    <div
      aria-hidden
      className="pointer-events-none fixed top-0 left-0 z-drag flex flex-col items-center gap-1"
      data-electron-no-drag-region
      data-favorite-ghost={leaving ? 'unpin' : 'move'}
      ref={refCallback}
      style={{
        ...NO_WINDOW_DRAG,
        transform: `translate(${initial.x - GHOST_HALF}px, ${initial.y - GHOST_HALF}px)`,
      }}
      title={title}
    >
      <span className="flex size-[42px] items-center justify-center rounded-xl border border-hairline-strong bg-card text-foreground shadow-lg">
        {icon}
      </span>
      {leaving && (
        <span className="type-caption flex items-center gap-1 rounded-sm bg-card px-2 py-0.5 text-muted-foreground shadow-sm">
          <PinOff className="size-3" />
          Unpin
        </span>
      )}
    </div>
  );
}

/** A place among the shown tiles: a favourite, or the gap a drag would drop into. */
export type FavoriteSlot = {
  entry: string;
  /** Null for the gap an app from All apps would take. */
  item: FavoriteItem | null;
  /**
   * `placeholder`: the dragged tile's place (an empty slot; the ghost carries it); `away`: the
   * dragged tile out of the tiles, hidden (kept in the page: a finger's touches stay on it).
   */
  state: 'tile' | 'placeholder' | 'away';
};

/**
 * One surface's favourites as a drop target: the tiles in their order, with the dragged one's
 * place where it would land (the others make room, animated unless motion is reduced). `layout`
 * is a grid (`perRow` tiles a row) or the rail's column.
 */
export function useFavoritesZone(
  favorites: Favorites,
  { layout, perRow = 1 }: { layout: 'grid' | 'column'; perRow?: number },
) {
  const id = useId();
  const element = useRef<HTMLElement | null>(null);
  const current = useSession();
  const [announcement, setAnnouncement] = useState('');
  const focusAfter = useRef<string | null>(null);
  const rects = useRef(new Map<string, DOMRect>());

  useEffect(() => {
    zones.set(id, {
      element,
      indexAt: (x, y, entry) =>
        element.current === null
          ? 0
          : slotIndex(element.current, x, y, entry, (slot) => {
              const gliding = slot.getAnimations?.().some((run) => run.playState === 'running');
              const laidOut = rects.current.get(slot.dataset['favoriteSlot'] ?? '');
              return gliding && laidOut !== undefined ? laidOut : slot.getBoundingClientRect();
            }),
    });
    return () => {
      zones.delete(id);
    };
  }, [id]);

  const { items } = favorites;
  let slots: FavoriteSlot[] = items.map((item) => ({ entry: item.entry, item, state: 'tile' }));
  if (current !== null) {
    const dragged = items.find((item) => item.entry === current.entry) ?? null;
    const rest = slots.filter((slot) => slot.entry !== current.entry);
    if (current.over?.zone === id) {
      const index = Math.max(0, Math.min(current.over.index, rest.length));
      rest.splice(index, 0, { entry: current.entry, item: dragged, state: 'placeholder' });
      slots = rest;
    } else if (current.from === 'favorites' && dragged !== null) {
      // Out of the tiles: they close up, it is unpinned if dropped there.
      slots = [...rest, { entry: current.entry, item: dragged, state: 'away' }];
    }
  }

  // The tiles that moved glide to their place; a tile moved by the keyboard keeps the focus.
  useLayoutEffect(() => {
    const root = element.current;
    if (root === null) return;
    const animate = current !== null && !reducedMotion();
    const next = new Map<string, DOMRect>();
    for (const slot of root.querySelectorAll<HTMLElement>(
      '[data-favorite-slot], [data-favorite-fixed]',
    )) {
      const key = slot.dataset['favoriteSlot'] ?? 'fixed';
      // Measured where it is laid out: a glide still under way ends here.
      for (const run of slot.getAnimations?.() ?? []) run.cancel();
      const rect = slot.getBoundingClientRect();
      next.set(key, rect);
      const previous = rects.current.get(key);
      if (
        animate &&
        previous !== undefined &&
        (previous.left !== rect.left || previous.top !== rect.top) &&
        typeof slot.animate === 'function'
      )
        slot.animate(
          [
            {
              transform: `translate(${previous.left - rect.left}px, ${previous.top - rect.top}px)`,
            },
            { transform: 'none' },
          ],
          { duration: 160, easing: 'ease-out' },
        );
    }
    rects.current = next;
    const focus = focusAfter.current;
    if (focus === null) return;
    focusAfter.current = null;
    for (const slot of root.querySelectorAll<HTMLElement>('[data-favorite-slot]'))
      if (slot.dataset['favoriteSlot'] === focus) slot.focus();
  });

  const moveTo = (item: FavoriteItem, index: number, keepFocus = false) => {
    const target = Math.max(0, Math.min(index, items.length - 1));
    if (target === items.indexOf(item)) return;
    favorites.move(item.entry, target);
    if (keepFocus) focusAfter.current = item.entry;
    setAnnouncement(`${item.title} moved to ${target + 1} of ${items.length}`);
  };

  /** Alt + arrows move the focused tile (a row up or down on a grid). */
  const onKeyDown = (event: ReactKeyboardEvent, item: FavoriteItem) => {
    if (!event.altKey) return;
    const steps: Record<string, number> =
      layout === 'grid'
        ? { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -perRow, ArrowDown: perRow }
        : { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -1, ArrowDown: 1 };
    const step = steps[event.key];
    if (step === undefined) return;
    event.preventDefault();
    moveTo(item, items.indexOf(item) + step, true);
  };

  return {
    ref: (node: HTMLElement | null) => {
      element.current = node;
    },
    slots,
    /** True while something is dragged over these tiles. */
    over: current?.over?.zone === id,
    moveTo,
    onKeyDown,
    announcement: (
      <span aria-live="polite" className="sr-only">
        {announcement}
      </span>
    ),
  };
}

/**
 * The tiles' menu (right-click, or a long press let go): on a tile, Move and Unpin; on a grid,
 * the tiles per row. All apps (always last) has no Move nor Unpin.
 */
export function FavoritesMenu({
  favorites,
  zone,
  layout,
  density,
  children,
}: {
  favorites: Favorites;
  zone: ReturnType<typeof useFavoritesZone>;
  layout: 'grid' | 'column';
  /** A grid's tiles per row are this density's (the sidebar's, the phone's). */
  density?: FavoritesDensity;
  children: ReactElement;
}) {
  const [target, setTarget] = useState<FavoriteItem | null>(null);
  const { items } = favorites;
  const index = target === null ? -1 : items.findIndex((item) => item.entry === target.entry);
  return (
    <ContextMenu>
      <ContextMenuTrigger
        asChild
        onContextMenu={(event) => {
          // A finger's long press lifts the tile; the menu waits for its release.
          if (touchPress && !openingMenu) {
            event.preventDefault();
            return;
          }
          const entry = (event.target as Element)
            .closest<HTMLElement>('[data-favorite-slot]')
            ?.getAttribute('data-favorite-slot');
          const item = items.find((candidate) => candidate.entry === entry) ?? null;
          // The rail has nothing to set but a tile's place.
          if (layout === 'column' && item === null) {
            event.preventDefault();
            return;
          }
          setTarget(item);
        }}
        onPointerDown={(event) => {
          // On a favourite, not the menu's own long press: the tile's (drag, or the menu on its
          // release). Elsewhere (All apps) the menu's own.
          if (
            event.pointerType !== 'mouse' &&
            (event.target as Element).closest('[data-favorite-slot]') !== null
          )
            event.preventDefault();
        }}
      >
        {children}
      </ContextMenuTrigger>
      <ContextMenuContent
        aria-label={target === null ? 'Favourites' : `${target.title} tile`}
        className="w-52"
      >
        {target !== null && index !== -1 && (
          <>
            <ContextMenuItem disabled={index === 0} onSelect={() => zone.moveTo(target, index - 1)}>
              {layout === 'grid' ? <ArrowLeft /> : <ArrowUp />}
              {layout === 'grid' ? 'Move left' : 'Move up'}
            </ContextMenuItem>
            <ContextMenuItem
              disabled={index === items.length - 1}
              onSelect={() => zone.moveTo(target, index + 1)}
            >
              {layout === 'grid' ? <ArrowRight /> : <ArrowDown />}
              {layout === 'grid' ? 'Move right' : 'Move down'}
            </ContextMenuItem>
            <ContextMenuItem onSelect={() => favorites.unpin(target.entry)}>
              <PinOff />
              Unpin
            </ContextMenuItem>
            {layout === 'grid' && <ContextMenuSeparator />}
          </>
        )}
        {layout === 'grid' && density !== undefined && (
          <>
            <ContextMenuLabel>Tiles per row</ContextMenuLabel>
            <ContextMenuRadioGroup
              onValueChange={(value) => favorites.setColumns(density, Number(value))}
              value={String(favorites.columns[density])}
            >
              {FAVORITE_COLUMNS[density].map((count) => (
                <ContextMenuRadioItem key={count} value={String(count)}>
                  {count}
                </ContextMenuRadioItem>
              ))}
            </ContextMenuRadioGroup>
          </>
        )}
      </ContextMenuContent>
    </ContextMenu>
  );
}
